import { findWikiCategory } from '../shared/wikiData';

/**
 * Pure model for the Wiki nav (spec A §1.3) — which row is selected and which
 * category must be open to show it. No React, no network: the nav component
 * feeds it the parsed route, the cached page doc and the category index.
 *
 * Selection follows the prototype exactly:
 *   Home / Ask the Wiki / To acknowledge / AI Brain   only on their own view
 *                                                      (never Home on every Wiki route)
 *   top-level category row                            only on that category's view
 *                                                      (NOT while one of its pages or
 *                                                      subcategories is open)
 *   subcategory row                                   on its own view AND while one of
 *                                                      its pages is open
 *
 * `expandId` is the top-level category that has to be expanded so the selected
 * subcategory row is visible (auto-expand to the active route). A selected
 * top-level row is always visible, so it needs none.
 */

const NONE = Object.freeze({ top: null, categoryId: null, subId: null, expandId: null });
const TOP_VIEWS = Object.freeze(['home', 'ask', 'ack', 'brain', 'archived']);

function pageDoc(page) {
    if (!page || typeof page !== 'object') return null;
    return page.page && typeof page.page === 'object' ? page.page : page;
}

/**
 * @param route  `parseWikiPath()` result (or null)
 * @param page   the cached `['wiki','page',id]` data for a page route (raw doc or `{page}`)
 * @param index  `{ byId }` from `useWikiCategories()` / `indexWikiCategories()`
 * @returns `{ top, categoryId, subId, expandId }`
 */
export function wikiNavActive(route, { page, index } = {}) {
    if (!route) return NONE;
    if (TOP_VIEWS.includes(route.view)) return { ...NONE, top: route.view };

    if (route.view === 'category') {
        const found = index ? findWikiCategory(index, route.id) : null;
        if (!found) return NONE;
        if (found.isSub) return { ...NONE, subId: found.category.id, expandId: found.parent.id };
        return { ...NONE, categoryId: found.category.id };
    }

    if (route.view === 'page') {
        const doc = pageDoc(page);
        // A stale cache entry for another page must not highlight anything.
        if (!doc || (doc.page_id && doc.page_id !== route.id)) return NONE;
        const subId = doc.subcategory_id || null;
        if (!subId) return NONE;
        const parentId = doc.category_id
            || (index ? findWikiCategory(index, subId)?.parent?.id : null)
            || null;
        return { ...NONE, subId, expandId: parentId };
    }

    return NONE;
}

/** Sidebar badge text: hidden (null) at 0 / unknown, "99+" above 99 (shell NavItem rule). */
export function navBadgeText(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n > 99 ? '99+' : String(Math.floor(n));
}
